/**
 * Mobile Filter Bottom Sheet Component
 * 
 * A mobile-optimized filter interface using a bottom sheet pattern.
 * Follows best practices for mobile filtering UI/UX.
 * 
 * @module MobileFilterSheet
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';

export interface MobileFilterOptions {
  province?: string;
  city?: string;
  salesGroup?: string;
  sortField?: string;
  status?: string;
}

interface MobileFilterSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: MobileFilterOptions) => void;
  availableProvinces: string[];
  availableCities: string[];
  availableSalesGroups?: string[];
  availableSortFields?: string[];
  availableStatuses?: string[];
  showStatusFilter?: boolean;
  showSalesGroupFilter?: boolean;
  showSortFieldFilter?: boolean;
  currentFilters?: MobileFilterOptions;
}

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity ${Transitions.duration.medium} ${Transitions.easing.easeInOut},
              visibility ${Transitions.duration.medium} ${Transitions.easing.easeInOut};
  backdrop-filter: blur(4px);
`;

const BottomSheet = styled.div<{ isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${ThemeColors.background.paper};
  border-radius: ${BorderRadius.xl} ${BorderRadius.xl} 0 0;
  box-shadow: ${Shadows.xl};
  z-index: 1001;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform ${Transitions.duration.medium} ${Transitions.easing.easeInOut};
  overflow: hidden;
`;

const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  position: sticky;
  top: 0;
  background-color: ${ThemeColors.background.paper};
  z-index: 10;
`;

const SheetTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${ThemeColors.text.secondary};
  cursor: pointer;
  border-radius: ${BorderRadius.md};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:active {
    background-color: ${ThemeColors.gray[100]};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${Spacing.lg};
  -webkit-overflow-scrolling: touch;
`;

const FilterSection = styled.div`
  margin-bottom: ${Spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterSectionHeader = styled.div<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.md};
  background-color: ${ThemeColors.gray[50]};
  border-radius: ${BorderRadius.md};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  margin-bottom: ${props => props.isExpanded ? Spacing.md : 0};

  &:active {
    background-color: ${ThemeColors.gray[100]};
  }
`;

const FilterSectionTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const FilterOptionsList = styled.div<{ isExpanded: boolean }>`
  display: ${props => props.isExpanded ? 'block' : 'none'};
  max-height: 300px;
  overflow-y: auto;
`;

const FilterOption = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${Spacing.md};
  border: none;
  background: ${props => props.selected ? ThemeColors.primary.light : 'transparent'};
  color: ${props => props.selected ? ThemeColors.primary.main : ThemeColors.text.primary};
  text-align: left;
  cursor: pointer;
  font-size: ${Typography.fontSize.md};
  border-radius: ${BorderRadius.sm};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  margin-bottom: ${Spacing.xs};

  &:active {
    background-color: ${props => props.selected ? ThemeColors.primary.light : ThemeColors.gray[50]};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterOptionLabel = styled.span`
  flex: 1;
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background-color: ${ThemeColors.primary.main};
  color: white;
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  margin-left: ${Spacing.sm};
`;

const SheetFooter = styled.div`
  display: flex;
  gap: ${Spacing.md};
  padding: ${Spacing.lg};
  border-top: 1px solid ${ThemeColors.border.light};
  background-color: ${ThemeColors.background.paper};
  position: sticky;
  bottom: 0;
  z-index: 10;
`;

const FooterButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${Spacing.md};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  background-color: ${props => 
    props.variant === 'primary' ? ThemeColors.primary.main : ThemeColors.background.paper
  };
  
  color: ${props => 
    props.variant === 'primary' ? ThemeColors.primary.contrastText : ThemeColors.text.primary
  };
  
  border: ${props => 
    props.variant === 'secondary' ? `1px solid ${ThemeColors.border.main}` : 'none'
  };

  &:active {
    opacity: 0.8;
    transform: scale(0.98);
  }
`;

const DragHandle = styled.div`
  width: 40px;
  height: 4px;
  background-color: ${ThemeColors.gray[300]};
  border-radius: ${BorderRadius.full};
  margin: ${Spacing.sm} auto ${Spacing.md};
`;

export const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({
  isOpen,
  onClose,
  onApply,
  availableProvinces,
  availableCities,
  availableSalesGroups = [],
  availableSortFields = [],
  availableStatuses = [],
  showStatusFilter = false,
  showSalesGroupFilter = false,
  showSortFieldFilter = false,
  currentFilters = {}
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState<MobileFilterOptions>(currentFilters);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    province: true,
    city: true,
    salesGroup: true,
    sortField: true,
    status: true
  });

  useEffect(() => {
    setLocalFilters(currentFilters);
  }, [currentFilters, isOpen]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFilterToggle = (type: keyof MobileFilterOptions, value: string) => {
    setLocalFilters(prev => {
      const currentValue = prev[type];
      if (currentValue === value) {
        const updated = { ...prev };
        delete updated[type];
        return updated;
      }
      return {
        ...prev,
        [type]: value
      };
    });
  };

  const handleClearAll = () => {
    setLocalFilters({});
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const getActiveFilterCount = () => {
    return Object.values(localFilters).filter(v => v !== undefined && v !== '').length;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      <Overlay isOpen={isOpen} onClick={handleOverlayClick} />
      <BottomSheet isOpen={isOpen}>
        <DragHandle />
        <SheetHeader>
          <SheetTitle>{t('storeFilter.title')}</SheetTitle>
          <CloseButton onClick={onClose} aria-label={t('common.close')}>
            <X size={20} />
          </CloseButton>
        </SheetHeader>

        <SheetContent>
          {showStatusFilter && availableStatuses.length > 0 && (
            <FilterSection>
              <FilterSectionHeader
                isExpanded={expandedSections.status}
                onClick={() => toggleSection('status')}
              >
                <FilterSectionTitle>{t('storeFilter.status')}</FilterSectionTitle>
                {expandedSections.status ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </FilterSectionHeader>
              <FilterOptionsList isExpanded={expandedSections.status}>
                <FilterOption
                  selected={!localFilters.status}
                  onClick={() => handleFilterToggle('status', '')}
                >
                  <FilterOptionLabel>{t('storeFilter.all')}</FilterOptionLabel>
                </FilterOption>
                {availableStatuses.map(status => (
                  <FilterOption
                    key={status}
                    selected={localFilters.status === status}
                    onClick={() => handleFilterToggle('status', status)}
                  >
                    <FilterOptionLabel>{t(`storeFilter.${status}`)}</FilterOptionLabel>
                    {localFilters.status === status && <FilterBadge>✓</FilterBadge>}
                  </FilterOption>
                ))}
              </FilterOptionsList>
            </FilterSection>
          )}

          <FilterSection>
            <FilterSectionHeader
              isExpanded={expandedSections.province}
              onClick={() => toggleSection('province')}
            >
              <FilterSectionTitle>{t('storeFilter.province')}</FilterSectionTitle>
              {expandedSections.province ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </FilterSectionHeader>
            <FilterOptionsList isExpanded={expandedSections.province}>
              <FilterOption
                selected={!localFilters.province}
                onClick={() => handleFilterToggle('province', '')}
              >
                <FilterOptionLabel>{t('storeFilter.allProvinces')}</FilterOptionLabel>
              </FilterOption>
              {availableProvinces.map(province => (
                <FilterOption
                  key={province}
                  selected={localFilters.province === province}
                  onClick={() => handleFilterToggle('province', province)}
                >
                  <FilterOptionLabel>{province}</FilterOptionLabel>
                  {localFilters.province === province && <FilterBadge>✓</FilterBadge>}
                </FilterOption>
              ))}
            </FilterOptionsList>
          </FilterSection>

          <FilterSection>
            <FilterSectionHeader
              isExpanded={expandedSections.city}
              onClick={() => toggleSection('city')}
            >
              <FilterSectionTitle>{t('storeFilter.city')}</FilterSectionTitle>
              {expandedSections.city ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </FilterSectionHeader>
            <FilterOptionsList isExpanded={expandedSections.city}>
              <FilterOption
                selected={!localFilters.city}
                onClick={() => handleFilterToggle('city', '')}
              >
                <FilterOptionLabel>{t('storeFilter.allCities')}</FilterOptionLabel>
              </FilterOption>
              {availableCities.map(city => (
                <FilterOption
                  key={city}
                  selected={localFilters.city === city}
                  onClick={() => handleFilterToggle('city', city)}
                >
                  <FilterOptionLabel>{city}</FilterOptionLabel>
                  {localFilters.city === city && <FilterBadge>✓</FilterBadge>}
                </FilterOption>
              ))}
            </FilterOptionsList>
          </FilterSection>

          {showSalesGroupFilter && availableSalesGroups.length > 0 && (
            <FilterSection>
              <FilterSectionHeader
                isExpanded={expandedSections.salesGroup}
                onClick={() => toggleSection('salesGroup')}
              >
                <FilterSectionTitle>{t('storeFilter.salesGroup')}</FilterSectionTitle>
                {expandedSections.salesGroup ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </FilterSectionHeader>
              <FilterOptionsList isExpanded={expandedSections.salesGroup}>
                <FilterOption
                  selected={!localFilters.salesGroup}
                  onClick={() => handleFilterToggle('salesGroup', '')}
                >
                  <FilterOptionLabel>{t('storeFilter.allSalesGroups')}</FilterOptionLabel>
                </FilterOption>
                {availableSalesGroups.map(group => (
                  <FilterOption
                    key={group}
                    selected={localFilters.salesGroup === group}
                    onClick={() => handleFilterToggle('salesGroup', group)}
                  >
                    <FilterOptionLabel>{group}</FilterOptionLabel>
                    {localFilters.salesGroup === group && <FilterBadge>✓</FilterBadge>}
                  </FilterOption>
                ))}
              </FilterOptionsList>
            </FilterSection>
          )}

          {showSortFieldFilter && availableSortFields.length > 0 && (
            <FilterSection>
              <FilterSectionHeader
                isExpanded={expandedSections.sortField}
                onClick={() => toggleSection('sortField')}
              >
                <FilterSectionTitle>{t('storeFilter.sortField')}</FilterSectionTitle>
                {expandedSections.sortField ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </FilterSectionHeader>
              <FilterOptionsList isExpanded={expandedSections.sortField}>
                <FilterOption
                  selected={!localFilters.sortField}
                  onClick={() => handleFilterToggle('sortField', '')}
                >
                  <FilterOptionLabel>{t('storeFilter.allSortFields', 'All Sort Fields')}</FilterOptionLabel>
                </FilterOption>
                {availableSortFields.map(field => (
                  <FilterOption
                    key={field}
                    selected={localFilters.sortField === field}
                    onClick={() => handleFilterToggle('sortField', field)}
                  >
                    <FilterOptionLabel>{field}</FilterOptionLabel>
                    {localFilters.sortField === field && <FilterBadge>✓</FilterBadge>}
                  </FilterOption>
                ))}
              </FilterOptionsList>
            </FilterSection>
          )}
        </SheetContent>

        <SheetFooter>
          <FooterButton variant="secondary" onClick={handleClearAll}>
            {t('storeFilter.clear')}
          </FooterButton>
          <FooterButton variant="primary" onClick={handleApply}>
            {t('storeFilter.apply')} {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          </FooterButton>
        </SheetFooter>
      </BottomSheet>
    </>
  );
};

